import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import useSWR from "swr";

import DexterLogo from "../../../img/new_navbar/Dexter.png";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LogoutIcon from "@mui/icons-material/Logout";
import BankInclusionApprovalModalContent from "./BankInclusionApprovalModalContent";
import RejectionReasonModal from "./RejectionReasonModal";
import CorrectionInstructionsModal from "./CorrectionInstructionsModal";
import { fetcher } from "../../../utilities/_fetcher";
import AxiosClient from "../../../Network/AxiosClient";
import { useNotification } from "../../Notifications/NotificationProvider";
import { signOut } from "../../../store/actions/authAction";

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}
function getUserRole(userType) {
  switch (userType) {
    case "cs_user_layer_1":
      return "CS L1";
    case "cs_user_layer_2":
      return "CS L2";
    default:
      return "User";
  }
}
const BankInclusionSessionView = () => {
  const [sessionStarted, setSessionStarted] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [selectedRejectionReasons, setSelectedRejectionReasons] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [noRequestsAvailable, setNoRequestsAvailable] = useState(false);
  const [currentActionType, setCurrentActionType] = useState(null);
  const [counts, setCounts] = useState({
    total: 0,
    approved: 0,
    rejected: 0,
  });

  const userType = JSON.parse(localStorage.getItem("currUserType"));
  const dispatchNotification = useNotification();
  const dispatch = useDispatch();
  const userAlias = localStorage.getItem("alias") ?? null;
  const canApproveReject = ["cs_user_layer_1", "cs_user_layer_2"].includes(
    userType
  );

  const sessionApiUrl = sessionStarted
    ? `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-details`
    : null;

  const shouldFetchCounts = sessionStarted && canApproveReject;
  const countApiUrl = shouldFetchCounts
    ? `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-count`
    : null;

  const { mutate: mutateSessionRequest } = useSWR(sessionApiUrl, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    refreshInterval: 0,
    onSuccess: (data) => {
      if (data && data.is_error === 1) {
        setNoRequestsAvailable(true);
        setCurrentRequest(null);
        return;
      }

      if (data && Object.keys(data).length > 0) {
        const requestKey = Object.keys(data)[0];
        const apiData = data[requestKey];
        const transformedData = {
          request_details: {
            request_id: requestKey,
            bank_info_id: requestKey,
            bank_name: apiData.request_details?.bank_name,
            branch_name: apiData.request_details?.bank_branch_name,
            bank_routing_number: apiData.request_details?.bank_routing_number,
            account_holder_name: apiData.request_details?.account_holder_name,
            account_number: apiData.request_details?.account_number,
            account_type: apiData.request_details?.account_type,
            micr_line_number: apiData.request_details?.micr_line_number,
            request_date: apiData.request_details?.request_date,
            bank_inclusion_form_image:
              apiData.images?.bank_inclusion_form_image || null,
            check_book_image: apiData.images?.check_book_image || null,
            has_l2_review: apiData.request_details?.has_l2_review || false,
          },
          merchant_details: {
            merchant_wallet: apiData.merchant_details?.wallet_number,
            merchant_name: apiData.merchant_details?.merchant_name,
            owner_name: apiData.merchant_details?.merchant_full_name,
            owner_phone: "N/A",
            address: apiData.merchant_details?.shop_address,
            reg_date: apiData.merchant_details?.registration_date,
            reg_tenure: apiData.merchant_details?.registration_tenure,
            merchant_type: apiData.merchant_details?.product_type,
            bmcc_number: apiData.merchant_details?.bmcc_code,
            bmcc_type: apiData.merchant_details?.bmcc_category,
            owner_image: apiData.images?.shop_owner_image || null,
            outlet_image: apiData.images?.outlet_image || null,
            requested_by: apiData.merchant_details?.requested_by || {},
          },
          bank_info_id: Object.values(data)[0]?.bank_info_id || null,
        };

        setCurrentRequest(transformedData);
      } else {
        setCurrentRequest(null);
        handleEndSession();
      }
    },
    onError: (error) => {
      console.error("Error fetching session request:", error);
      setCurrentRequest(null);
      handleEndSession();
    },
  });

  const { mutate: mutateCountDataLocal } = useSWR(countApiUrl, fetcher, {
    onSuccess: (data) => {
      if (data) {
        setCounts({
          total: data.total || 0,
          approved: data.approved || 0,
          rejected: data.rejected || 0,
          sent_back: data.sent_back || 0,
        });
      }
    },
    onError: () => {
      setCounts({
        total: 0,
        approved: 0,
        rejected: 0,
        sent_back: 0,
      });
    },
    revalidateOnFocus: false,
    refreshInterval: 0,
  });

  const handleStartSession = useCallback(() => {
    setSessionStarted(true);
    setNoRequestsAvailable(false);
  }, []);

  const handleEndSession = useCallback(() => {
    setSessionStarted(false);
    setCurrentRequest(null);
    setShowRejectionModal(false);
    setShowCorrectionModal(false);
    setNoRequestsAvailable(false);
    setCurrentActionType(null);
  }, []);

  const submitReview = useCallback(
    async (reviewData) => {
      try {
        setIsProcessing(true);

        const token = JSON.parse(localStorage.getItem("currUserToken"));
        await AxiosClient.post(
          `${process.env.REACT_APP_API_URL}/api/bank-inclusion/v1/qc-review`,
          reviewData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const actionMessage =
          reviewData.reason_type === "accept" ? "approved" : "rejected";
        dispatchNotification({
          type: "SUCCESS",
          message: `Request ${actionMessage} successfully`,
        });

        return true;
      } catch (error) {
        console.error("Error submitting review:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to submit review. Please try again.";

        dispatchNotification({
          type: "ERROR",
          message: errorMessage,
        });

        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    [dispatchNotification]
  );

  const handleApprove = useCallback(
    async (editedRequestData) => {
      if (!currentRequest || isProcessing) return;

      const filterNAValue = (value) => {
        return value === "N/A" ? "" : value || "";
      };

      const dataToUse = editedRequestData || currentRequest;

      const reviewData = {
        bank_info_id: parseInt(dataToUse.request_details?.bank_info_id),
        reason_type: "accept",
        reasons: null,
        correction_instructions: null,
        shop_owner_name: filterNAValue(
          dataToUse.request_details?.account_holder_name
        ),
        bank_account_number: filterNAValue(
          dataToUse.request_details?.account_number
        ),
        micr_line_number: filterNAValue(
          dataToUse.request_details?.micr_line_number
        ),
      };
      const success = await submitReview(reviewData);
      if (success) {
        setCurrentRequest(null);
        await mutateSessionRequest();
        if (mutateCountDataLocal) {
          mutateCountDataLocal();
        }
      }
    },
    [
      currentRequest,
      isProcessing,
      submitReview,
      mutateSessionRequest,
      mutateCountDataLocal,
    ]
  );

  const handleReasonsSubmit = useCallback(async (selectedReasons) => {
    setSelectedRejectionReasons(selectedReasons);
    setShowRejectionModal(false);
    setShowCorrectionModal(true);
  }, []);

  const handleCorrectionSubmit = useCallback(
    async (instructions) => {
      if (!currentRequest || isProcessing) return;

      const reasonsArray = selectedRejectionReasons.map((reason) => {
        return typeof reason === "string"
          ? reason
          : reason.value || reason.label;
      });

      const reasonType =
        currentActionType === "send_back" ? "sent_back" : "reject";

      const reviewData = {
        bank_info_id: parseInt(currentRequest.request_details?.bank_info_id),
        reason_type: reasonType,
        reasons: reasonsArray,
        correction_instructions: instructions,
        shop_owner_name:
          currentRequest.request_details?.account_holder_name || null,
        bank_account_number:
          currentRequest.request_details?.account_number || null,
        micr_line_number:
          currentRequest.request_details?.micr_line_number || null,
      };

      const success = await submitReview(reviewData);
      if (success) {
        setSelectedRejectionReasons([]);
        setCurrentActionType(null);
        setShowCorrectionModal(false);
        setShowRejectionModal(false);
        mutateSessionRequest();
        mutateCountDataLocal();
      }
    },
    [
      selectedRejectionReasons,
      currentRequest,
      isProcessing,
      submitReview,
      mutateSessionRequest,
      mutateCountDataLocal,
      currentActionType,
    ]
  );

  const handleSignOut = useCallback(() => {
    dispatch(signOut());
  }, [dispatch]);

  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     if (!sessionStarted || isProcessing) {
  //       if (e.code === "Space" && !isProcessing && !sessionStarted) {
  //         e.preventDefault();
  //         handleStartSession();
  //       }
  //       return;
  //     }

  //     if (
  //       showRejectionModal ||
  //       showCorrectionModal ||
  //       isAnyFieldEditing ||
  //       !currentRequest
  //     ) {
  //       return;
  //     }

  //     if (e.code === "Space") {
  //       e.preventDefault();
  //       handleApprove();
  //     }

  //     if (e.key === "Control" && e.ctrlKey) {
  //       e.preventDefault();
  //       setShowRejectionModal(true);
  //     }
  //   };

  //   window.addEventListener("keydown", handleKeyDown);
  //   return () => window.removeEventListener("keydown", handleKeyDown);
  // }, [
  //   sessionStarted,
  //   showRejectionModal,
  //   showCorrectionModal,
  //   handleStartSession,
  //   handleApprove,
  //   isProcessing,
  //   isAnyFieldEditing,
  //   currentRequest,
  // ]);

  if (!sessionStarted) {
    return (
      <div className="bank-inclusion-session-container">
        <div className="bank-inclusion-session-card">
          <div className="bank-inclusion-session-details">
            <img className="dexter-logo" src={DexterLogo} alt="dexter-icon" />
            <div className="user-greetings">
              <h1 className="welcome-text">Welcome</h1>
              <h2 className="username">
                {userAlias && capitalizeFirstLetter(JSON.parse(userAlias))}
              </h2>
              <p className="user-role">{getUserRole(userType)}</p>
            </div>
          </div>

          <div className="bank-inclusion-session-btn">
            <button className="start-session-btn" onClick={handleStartSession}>
              {/* <div className="start-session-btn-keyboard-hint">
                <span className="press-space-text">
                  Press "Space" on your keyboard to
                </span>
              </div> */}
              <div className="start-session-btn-text">
                <span className="start-session-text">Start Session</span>
                <PlayArrowIcon />
              </div>
            </button>
          </div>

          <div className="sign-out-section">
            <button className="sign-out-btn" onClick={handleSignOut}>
              <LogoutIcon />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bank-inclusion-session-active">
      <BankInclusionApprovalModalContent
        requestData={currentRequest}
        loading={!noRequestsAvailable && !currentRequest}
        error={false}
        noRequestsAvailable={noRequestsAvailable}
        onClose={handleEndSession}
        onReject={() => {
          if (!isProcessing) {
            setCurrentActionType("reject");
            setShowRejectionModal(true);
          }
        }}
        onSendBack={() => {
          if (!isProcessing) {
            setCurrentActionType("send_back");
            setShowRejectionModal(true);
          }
        }}
        onApprove={(editedData) => !isProcessing && handleApprove(editedData)}
        isSessionMode={true}
        isProcessing={isProcessing}
        showRejectionModal={showRejectionModal}
        showCorrectionModal={showCorrectionModal}
        counts={counts}
        showL2FeedbackInitialValue={
          currentRequest?.request_details?.has_l2_review || false
        }
      />

      {showRejectionModal && (
        <RejectionReasonModal
          onClose={() => !isProcessing && setShowRejectionModal(false)}
          onReasonsSubmit={handleReasonsSubmit}
          actionType={currentActionType}
        />
      )}

      {showCorrectionModal && (
        <CorrectionInstructionsModal
          onClose={() => !isProcessing && setShowCorrectionModal(false)}
          onSubmit={handleCorrectionSubmit}
          title="Write correction instructions."
          message={
            currentActionType === "send_back"
              ? "Please write correction instructions to help guide rectification."
              : userType === "cs_user_layer_1"
                ? "Are you sure you want to reject this bank inclusion request? Please write correction instructions in case this request needs to be raised again."
                : "Please write correction instructions to help guide rectification."
          }
          placeholder="Correction Instructions"
          submitButtonText={
            currentActionType === "send_back" ? "Send Back to L1" : "Reject"
          }
          submitButtonClass="btn--reject"
          minHeight={120}
          maxLength={1000}
          showCharacterCount={false}
          required={false}
        />
      )}
    </div>
  );
};

export default BankInclusionSessionView;
